/**
 * "V2 — Agency Display" — the agency's Display template.
 *
 * It starts from the White Label Default display layout — the 2026 Display mockup expressed
 * declaratively (`docs/mockups/Sample Report Mockup-Display (1).pdf`) — and diverges from it on two
 * agency-requested points. Everything else tracks the white-label default by construction, so a fix
 * to the shared layout still reaches both.
 *
 * ── The two divergences ──
 *
 *  1. HEADER right-hand label shows the CAMPAIGN NAME (`campaign.name`), not the client name, and the
 *     id below it is labelled "CAMPAIGN IO#:" (insertion order) rather than the default "CAMPAIGN ID#:".
 *     The white-label default names the client there and keeps the "ID#" label the mockup uses.
 *
 *  2. The identity-row CREATIVE shows a DELIVERED creative, so the artwork sits on the left and the
 *     meta card shifts right — as the mockup has it. The white-label binding points at
 *     `campaign.bannerAssetId`, which the generate worker does not populate, so the block hid and the
 *     meta card ran full width. This prefers `campaign.bannerAssetId` when set; otherwise it uses the
 *     delivered 300×250 medium rectangle when one exists, and failing that the LARGEST delivered
 *     creative by pixel area, falling back to the first creative that carries an asset.
 *
 * Built as a deep, independent clone with targeted overrides rather than a 240-line re-transcription:
 * the pixels stay identical everywhere the agency did not ask to change, and the two divergences are
 * the only edits in the file, so what differs from the mockup is legible at a glance. Rule 4 holds —
 * this is a declarative document, not a component.
 */

import type { Template } from './template-schema.js';
import { DISPLAY_TEMPLATE } from './display-template.js';

// Deep, independent clone of the proven layout. The template is pure JSON data, so a round-trip is a
// faithful deep copy that shares no references with DISPLAY_TEMPLATE — an edit here cannot mutate the
// white-label default every client shares.
const base = JSON.parse(JSON.stringify(DISPLAY_TEMPLATE)) as Template;

// (1) Header right-hand label → the campaign name, and the id label → "CAMPAIGN IO#:".
const header = base.chrome.header.find((b) => b.type === 'PageHeader');
if (header) {
  header.bindings = { ...header.bindings, clientName: 'campaign.name' };
  header.props = { ...header.props, campaignIdLabel: 'CAMPAIGN IO#:' };
}

// (2) Identity-row creative → a delivered creative: the 300×250 if present, else the largest by area.
//     The creative dimensions on the report model are `widthPx` / `heightPx` (an earlier version read
//     `width`/`height`, which never matched, so every report fell through to the first creative).
//     `campaign.bannerAssetId` still wins outright; no creatives resolves to nothing (block hides).
const CREATIVE_ASSET =
  'campaign.bannerAssetId ? campaign.bannerAssetId : (' +
  '$exists((adGroups.creatives[assetId])[widthPx = 300 and heightPx = 250][0]) ' +
  '? (adGroups.creatives[assetId])[widthPx = 300 and heightPx = 250][0].assetId ' +
  ': ((adGroups.creatives[assetId])^(>(($exists(widthPx) and $exists(heightPx)) ? widthPx * heightPx : -1)))[0].assetId)';
const identityRow = base.pages[0].blocks.find((b) => b.id === 'identity-row');
const creative = identityRow?.children?.find((c) => c.id === 'creative');
if (creative) creative.bindings = { ...creative.bindings, assetId: CREATIVE_ASSET };

// (3) Identity CARD id row is labelled "Campaign IO", to match the header's IO# treatment.
const meta = identityRow?.children?.find((c) => c.id === 'meta');
if (meta) meta.props = { ...meta.props, campaignIdLabel: 'Campaign IO' };

// (4) Geographic Targeting renders the OFFLINE OPEN-SOURCE SVG map instead of a Google static image.
//     Binding `geoMode` switches the block into map mode; it defaults to 'zip' when the report carries
//     ZIP targeting (all real reports today), so the open-source map replaces Google without waiting on
//     the other modes' data. The entity lists feed state/county/city/DMA once that targeting is wired.
const geoMap = base.pages[1].blocks.find((b) => b.id === 'geo-card')?.children?.find((c) => c.id === 'geo-map');
if (geoMap) {
  geoMap.bindings = {
    ...geoMap.bindings,
    mapImage: 'targeting.mapImage',
    geoMode: "targeting.geoMode ? targeting.geoMode : ($count(targeting.zips) > 0 ? 'zip' : '')",
    states: 'targeting.states',
    counties: 'targeting.counties',
    cities: 'targeting.cities',
    dmas: 'targeting.dmas',
  };
}

// (5) V2 icon set: the shared cards (Client/Start/End rows, Device Category/Details, Channels, Browser)
//     and the template's KPI/section marks render the approved raster icons on this report. The plain
//     white-label default it clones leaves this unset, so it stays on the legacy glyphs — unchanged.
base.theme = { ...base.theme, iconStyle: 'v2' };

export const AGENCY_DISPLAY_TEMPLATE: Template = {
  ...base,
  id: 'v2-agency-display',
  name: 'V2 — Agency Display',
  version: 1,
  createdAt: '2026-08-25T00:00:00.000Z',
  createdBy: 'agency-display-request',
};
